import { Lock } from "lucide-react";
import { FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { AddressFields } from "@/components/forms/AddressFields";
import { MaskedSensitiveField } from "./MaskedSensitiveField";
import { CollapsibleSection } from "./CollapsibleSection";
import type {
  CreateBusinessRequest,
  EntityTypeCode,
  LeaseOrOwn,
} from "@/types/counterparties";
import { EMPTY_ADDRESS } from "@/types/counterparties";
import { US_STATES } from "@/lib/usStates";

export type BusinessFormState = CreateBusinessRequest;

export const EMPTY_BUSINESS_FORM: BusinessFormState = {
  legalEntityName: "",
  dba: "",
  entityType: "LLC",
  stateOfFormation: "",
  dateOfFormation: null,
  ein: null,
  naicsCode: "",
  industryDescription: "",
  yearsInOperation: null,
  numberOfLocations: null,
  numberOfEmployees: null,
  annualRevenuePreliminary: null,
  annualEBITDAPreliminary: null,
  recurringRevenuePct: null,
  customerConcentrationTop5Pct: null,
  leaseOrOwn: null,
  leaseExpirationDate: null,
  hqAddress: EMPTY_ADDRESS,
  additionalLocationsJson: null,
  website: "",
  briefDescription: "",
  tags: [],
};

const ENTITY_TYPES: EntityTypeCode[] = ["LLC", "SCorp", "CCorp", "LP", "LLP", "Trust", "SoleProp", "Other"];
const LEASE_OWN: LeaseOrOwn[] = ["Lease", "Own", "Mixed"];

interface BusinessFormProps {
  value: BusinessFormState;
  onChange: (next: BusinessFormState) => void;
  einMasked?: string | null;
  editingEin?: boolean;
  onEditEin?: () => void;
}

export function BusinessForm({
  value,
  onChange,
  einMasked,
  editingEin,
  onEditEin,
}: BusinessFormProps) {
  const set = <K extends keyof BusinessFormState>(k: K, v: BusinessFormState[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className="flex flex-col gap-space-4">
      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="Legal entity name" required className="md:col-span-2">
            <Input value={value.legalEntityName} onChange={(e) => set("legalEntityName", e.target.value)} />
          </FormField>
          <FormField label="DBA" className="md:col-span-2">
            <Input value={value.dba ?? ""} onChange={(e) => set("dba", e.target.value || null)} />
          </FormField>
          <FormField label="Entity type" required>
            <Select value={value.entityType} onChange={(e) => set("entityType", e.target.value as EntityTypeCode)}>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
          <FormField label="State of formation" required>
            <Select
              value={value.stateOfFormation}
              onChange={(e) => set("stateOfFormation", e.target.value)}
            >
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </Select>
          </FormField>
          <FormField label="Date of formation">
            <Input
              type="date"
              value={value.dateOfFormation ?? ""}
              onChange={(e) => set("dateOfFormation", e.target.value || null)}
            />
          </FormField>
          <FormField label="EIN" helperText="Stored encrypted; masked on read.">
            <MaskedSensitiveField
              label="EIN"
              masked={einMasked}
              editing={editingEin || !einMasked}
              editingValue={value.ein ?? ""}
              onChange={(v) => set("ein", v || null)}
              onRequestEdit={onEditEin}
              placeholder="12-3456789"
            />
          </FormField>
          <FormField label="Website" className="md:col-span-2">
            <Input value={value.website ?? ""} onChange={(e) => set("website", e.target.value || null)} />
          </FormField>
        </div>
      </Section>

      <Section title="Industry & operations">
        <div className="flex flex-col gap-space-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
            <FormField label="NAICS code" required>
              <Input
                value={value.naicsCode}
                onChange={(e) => set("naicsCode", e.target.value)}
                placeholder="6 digits"
                maxLength={10}
              />
            </FormField>
            <FormField label="Industry description" required className="md:col-span-3">
              <Input
                value={value.industryDescription}
                onChange={(e) => set("industryDescription", e.target.value)}
              />
            </FormField>
            <FormField label="Years in operation">
              <Input
                type="number"
                min={0}
                value={value.yearsInOperation ?? ""}
                onChange={(e) => set("yearsInOperation", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label="# locations">
              <Input
                type="number"
                min={0}
                value={value.numberOfLocations ?? ""}
                onChange={(e) => set("numberOfLocations", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label="# employees">
              <Input
                type="number"
                min={0}
                value={value.numberOfEmployees ?? ""}
                onChange={(e) => set("numberOfEmployees", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label="Annual revenue (USD)">
              <Input
                type="number"
                step="0.01"
                value={value.annualRevenuePreliminary ?? ""}
                onChange={(e) => set("annualRevenuePreliminary", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
            <FormField label="Annual EBITDA (USD)">
              <Input
                type="number"
                step="0.01"
                value={value.annualEBITDAPreliminary ?? ""}
                onChange={(e) => set("annualEBITDAPreliminary", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label="Recurring revenue (%)">
              <Input
                type="number"
                step="0.1"
                min={0}
                max={100}
                value={value.recurringRevenuePct ?? ""}
                onChange={(e) => set("recurringRevenuePct", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
            <FormField label="Top-5 customer concentration (%)">
              <Input
                type="number"
                step="0.1"
                min={0}
                max={100}
                value={value.customerConcentrationTop5Pct ?? ""}
                onChange={(e) => set("customerConcentrationTop5Pct", e.target.value ? Number(e.target.value) : null)}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
            <FormField label="Lease or own">
              <Select
                value={value.leaseOrOwn ?? ""}
                onChange={(e) => set("leaseOrOwn", (e.target.value || null) as LeaseOrOwn | null)}
              >
                <option value="">—</option>
                {LEASE_OWN.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </FormField>
            <FormField label="Lease expiration">
              <Input
                type="date"
                value={value.leaseExpirationDate ?? ""}
                onChange={(e) => set("leaseExpirationDate", e.target.value || null)}
              />
            </FormField>
          </div>
        </div>
      </Section>

      <Section title="HQ address">
        <AddressFields
          value={value.hqAddress}
          onChange={(a) => set("hqAddress", a)}
          required
          labels={{ city: "HQ city", state: "HQ state" }}
          idPrefix="biz-hq"
        />
      </Section>

      <CollapsibleSection
        title="Description & additional locations"
        warning={<span className="inline-flex items-center gap-space-1"><Lock size={12} aria-hidden /> Free-form fields.</span>}
      >
        <FormField label="Brief description">
          <Textarea
            rows={3}
            value={value.briefDescription ?? ""}
            onChange={(e) => set("briefDescription", e.target.value || null)}
          />
        </FormField>
        <FormField
          label="Additional locations (JSON)"
          className="mt-space-3"
          helperText='Free-form JSON array, e.g. [{"city":"Reno","state":"NV"}]. Optional.'
        >
          <Textarea
            rows={3}
            value={value.additionalLocationsJson ?? ""}
            onChange={(e) => set("additionalLocationsJson", e.target.value || null)}
          />
        </FormField>
      </CollapsibleSection>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-2 mt-0">
        {title}
      </h3>
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
        {children}
      </div>
    </section>
  );
}
